/**
 * Política de Privacidade — página pública, sem login.
 *
 * Existe por exigência concreta: a Meta só publica um app (e só aceita análise
 * de permissões do Instagram) com URL pública de política de privacidade e com
 * instruções de exclusão de dados. Mas o texto não é formalidade de fachada —
 * ele descreve o que o sistema REALMENTE guarda, incluindo o que é sensível
 * (tokens, credenciais da Shopee, comentários de terceiros) e como.
 *
 * Escrita em primeira pessoa do plural e em português claro, porque quem lê é o
 * cliente afiliado e o revisor da Meta, não um jurista. Cada afirmação aqui é
 * verificável no código: se algo mudar no que coletamos, esta página muda junto.
 */

import { useEffect } from 'react';

const ATUALIZADA_EM = '26 de agosto de 2026';
const CONTATO = 'gustavo@gugacloud.com.br';

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="doc-secao">
      <h2>{titulo}</h2>
      {children}
    </section>
  );
}

export function Privacidade({ onVoltar }: { onVoltar: () => void }) {
  /**
   * Rola até a âncora depois de montar.
   *
   * O salto nativo do navegador acontece ANTES do React montar a página, então
   * `/privacidade#exclusao` — a URL que fica cadastrada na Meta como
   * "instruções de exclusão de dados" — caía no topo do documento. Quem clica
   * ali é o revisor da análise, procurando exatamente aquela seção.
   */
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    const alvo = document.getElementById(id);
    if (alvo) alvo.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div className="landing">
      <div className="doc-pagina">
        <button type="button" className="ghost doc-voltar" onClick={onVoltar}>
          ← Voltar
        </button>

        <p className="doc-eyebrow mono">Automação Shopee</p>
        <h1 className="doc-titulo">Política de Privacidade</h1>
        <p className="muted">Atualizada em {ATUALIZADA_EM}</p>

        <Secao titulo="Quem somos e o que este serviço faz">
          <p>
            A <strong>Automação Shopee</strong> é uma ferramenta que ajuda afiliados da
            Shopee a divulgar produtos: encontra ofertas na API oficial de afiliados,
            gera o texto da publicação, agenda e publica em Instagram, TikTok e WhatsApp,
            e mede o resultado em comissão.
          </p>
          <p>
            O serviço é operado por <strong>GugaCloud</strong>. Cada cliente tem sua
            própria conta, com dados isolados dos demais.
          </p>
        </Secao>

        <Secao titulo="Que dados coletamos">
          <p>Coletamos apenas o necessário para o serviço funcionar:</p>
          <table className="doc-tabela">
            <thead>
              <tr>
                <th>Dado</th>
                <th>Para quê</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nome, e-mail e senha da conta</td>
                <td>
                  Identificar você e permitir o acesso. A senha é guardada como hash
                  Argon2 — não temos como lê-la.
                </td>
              </tr>
              <tr>
                <td>Credenciais da Shopee (App ID e chave)</td>
                <td>Buscar ofertas e gerar seus links de afiliado.</td>
              </tr>
              <tr>
                <td>Token de acesso do Instagram e ID da conta</td>
                <td>
                  Publicar as postagens que você agenda e responder comentários com o
                  link do produto, quando você liga essa automação.
                </td>
              </tr>
              <tr>
                <td>Sessão do WhatsApp (dispositivo pareado)</td>
                <td>Enviar as publicações para os grupos que você escolher.</td>
              </tr>
              <tr>
                <td>
                  Comentários públicos nos seus posts: texto, nome de usuário e
                  identificador do comentário
                </td>
                <td>
                  Reconhecer quem pediu o link e evitar responder a mesma pessoa duas
                  vezes.
                </td>
              </tr>
              <tr>
                <td>Fotos e vídeos que você envia</td>
                <td>Compor as publicações.</td>
              </tr>
              <tr>
                <td>Relatórios de conversão da Shopee</td>
                <td>Mostrar quanto cada publicação rendeu.</td>
              </tr>
            </tbody>
          </table>
          <p>
            <strong>Não coletamos</strong> mensagens privadas suas ou de terceiros que
            não sejam as respostas automáticas que o próprio sistema envia, não lemos sua
            caixa de entrada, não acessamos contatos e não usamos rastreadores de
            publicidade de terceiros.
          </p>
        </Secao>

        <Secao titulo="Dados de outras pessoas">
          <p>
            Quando alguém comenta em uma publicação sua e a automação de respostas está
            ligada, guardamos <strong>o texto do comentário, o nome de usuário público e
            o identificador do comentário</strong>. Isso serve a dois propósitos, ambos
            necessários: enviar o link pedido e impedir que a mesma pessoa receba a mesma
            mensagem repetidas vezes.
          </p>
          <p>
            Não guardamos foto de perfil, lista de seguidores, e-mail nem telefone dessas
            pessoas. Esses registros são apagados junto com a conta e podem ser apagados
            a pedido — inclusive a pedido da própria pessoa que comentou, pelo contato
            abaixo.
          </p>
        </Secao>

        <Secao titulo="Como protegemos">
          <ul>
            <li>
              Credenciais e tokens de terceiros (Shopee, Instagram, TikTok) são
              guardados <strong>cifrados com AES-256-GCM</strong>. Eles nunca voltam para
              a tela nem aparecem em respostas da nossa API.
            </li>
            <li>Todo o tráfego é por HTTPS.</li>
            <li>
              Cada conta só enxerga os próprios dados. Toda consulta ao banco é
              delimitada pela conta de quem está autenticado.
            </li>
            <li>
              O acesso administrativo a uma conta de cliente, quando necessário para
              suporte, é registrado e limitado no tempo.
            </li>
          </ul>
        </Secao>

        <Secao titulo="Com quem compartilhamos">
          <p>
            Não vendemos nem alugamos seus dados. Compartilhamos apenas com os serviços
            necessários para a operação:
          </p>
          <ul>
            <li>
              <strong>Meta (Instagram)</strong> — para publicar e responder em seu nome.
            </li>
            <li>
              <strong>Shopee</strong> — para buscar ofertas e gerar seus links.
            </li>
            <li>
              <strong>Microsoft Azure</strong> — hospedagem da aplicação, do banco de
              dados e das mídias.
            </li>
            <li>
              <strong>Anthropic</strong> — geração dos textos. Enviamos o nome, o preço e
              a categoria do produto. <strong>Não</strong> enviamos seus dados pessoais,
              suas credenciais nem dados de quem comentou.
            </li>
          </ul>
        </Secao>

        <Secao titulo="Por quanto tempo guardamos">
          <p>
            Enquanto sua conta existir. Publicações, relatórios e histórico de respostas
            ficam disponíveis para você consultar o desempenho ao longo do tempo. Ao
            encerrar a conta, tudo é apagado conforme a seção seguinte.
          </p>
        </Secao>

        <Secao titulo="Como excluir seus dados">
          <p id="exclusao">
            Você pode pedir a exclusão a qualquer momento, e não cobramos nada por isso.
          </p>
          <ol>
            <li>
              Envie um e-mail para <strong>{CONTATO}</strong> com o assunto{' '}
              <em>“Exclusão de dados”</em>, a partir do e-mail cadastrado na conta.
            </li>
            <li>
              Confirmamos o recebimento em até <strong>2 dias úteis</strong>.
            </li>
            <li>
              A exclusão é concluída em até <strong>30 dias</strong> e abrange: conta e
              usuários, credenciais e tokens, produtos, publicações, mídias enviadas,
              relatórios de conversão e o histórico de respostas automáticas.
            </li>
          </ol>
          <p>
            Você também pode revogar nosso acesso a qualquer momento, sem passar por nós:
            no Instagram, em <em>Configurações → Sites e aplicativos</em>; no Business
            Suite, removendo o acesso do app aos seus ativos. Feito isso, paramos de
            publicar e de responder imediatamente.
          </p>
          <p>
            Se você é uma pessoa que <strong>comentou</strong> em uma publicação de um de
            nossos clientes e quer que seu comentário e nome de usuário sejam apagados do
            nosso registro, escreva para o mesmo endereço — não é preciso ter conta
            conosco.
          </p>
        </Secao>

        <Secao titulo="Seus direitos">
          <p>
            Pela LGPD (Lei 13.709/2018), você pode pedir confirmação de tratamento,
            acesso, correção, portabilidade, anonimização e exclusão dos seus dados, além
            de revogar o consentimento. Basta escrever para <strong>{CONTATO}</strong>.
          </p>
        </Secao>

        <Secao titulo="Cookies">
          <p>
            Usamos apenas armazenamento local do navegador para manter você conectado e
            lembrar preferências de uso (tema claro/escuro e a fila de trabalho em
            andamento). Não usamos cookies de publicidade nem rastreamento entre sites.
          </p>
        </Secao>

        <Secao titulo="Mudanças nesta política">
          <p>
            Se algo mudar no que coletamos ou em como usamos, atualizamos esta página e a
            data no topo. Mudanças relevantes são avisadas por e-mail aos clientes.
          </p>
        </Secao>

        <Secao titulo="Contato">
          <p>
            Dúvidas, pedidos de acesso ou exclusão: <strong>{CONTATO}</strong>
          </p>
        </Secao>
      </div>
    </div>
  );
}
